// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCreateAppRewardConfiguration } from '@hooks/mutations/registry/configuration/useCreateAppRewardConfiguration';
import { useAppRewardConfiguration } from '@hooks/mutations/registry/onboarding/useAppRewardConfiguration';
import React from 'react';
import { Box, TextField, Typography } from '@mui/material';
import FormDialog from '../../components/Dialog/FormDialog';
import { TextInputLabel } from '../../components/Form/TextInput';
import { ProviderSelect } from '../../components/ProviderSelect';
import { useParties } from '../../contexts/PartiesContext';
import { isPositiveFloat } from '../../utils/isPositiveFloat';
import { DialogProps } from './DialogProps';

interface CreateAppRewardConfigurationDialogProps extends DialogProps {
  provider?: string;
}
export const CreateAppRewardConfigurationDialog: React.FC<
  CreateAppRewardConfigurationDialogProps
> = ({ open, onClose, provider }) => {
  const create = useCreateAppRewardConfiguration();
  const rewardConfigs = useAppRewardConfiguration();
  const { party, dso, operator } = useParties();

  const fields = { beneficiary: '', weight: '', provider: provider || '' };
  const [form, setForm] = React.useState(fields);
  const [showAmountError, setShowError] = React.useState(false);
  const onCloseForm = () => {
    setForm(fields);
    onClose();
  };
  const onFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'weight') {
      setShowError(false);
      const w = e.target.value;
      if (isPositiveFloat(w)) {
        setForm((p) => ({ ...p, weight: e.target.value }));
        const decimal = parseFloat(w);
        if (decimal > 1) {
          setShowError(true);
        }
      }
    } else {
      setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    }
  };
  const onProviderChange = (inputProvider: string) => {
    setForm((p) => ({ ...p, provider: inputProvider }));
  };

  const providerAlreadyHasConfiguration = React.useMemo(() => {
    if (!form.provider) {
      return false;
    }
    return (rewardConfigs.data || []).some((c) => c.payload.provider === form.provider);
  }, [form.provider, rewardConfigs.data]);

  const checkingProviderUniqueness = !!form.provider && rewardConfigs.isLoading;

  const areConfigDetailsComplete = form.weight && form.beneficiary && form.provider;
  const submitDisabled =
    !areConfigDetailsComplete ||
    showAmountError ||
    checkingProviderUniqueness ||
    providerAlreadyHasConfiguration;
  const onCreate = async () => {
    if (providerAlreadyHasConfiguration) {
      return;
    }
    await create({
      submitter: party,
      argument: {
        operator,
        provider: form.provider,
        details: {
          dso,
          operatorAppRewardBeneficiary: { beneficiary: form.beneficiary, weight: form.weight },
        },
      },
    });
    onClose();
  };
  return (
    <FormDialog
      title="Create App Reward Configuration"
      onSubmit={onCreate}
      submitName="Create"
      open={open}
      isSubmitDisabled={submitDisabled}
      onCancel={onCloseForm}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <TextInputLabel label="Beneficiary" />
        <TextField
          onChange={onFieldChange}
          value={form.beneficiary}
          sx={{ mb: 1 }}
          size="small"
          name="beneficiary"
          placeholder="Beneficiary"
        />
        <TextInputLabel label="Provider" />

        <Box sx={{ mb: 1 }}>
          <ProviderSelect
            onInputChange={onProviderChange}
            selectedProvider={form.provider}
            onChange={onProviderChange}
          />
          {checkingProviderUniqueness && (
            <Typography variant="caption">Checking provider configuration…</Typography>
          )}
          {providerAlreadyHasConfiguration && (
            <Typography variant="caption" color="error">
              An App Reward Configuration already exists for this provider.
            </Typography>
          )}
        </Box>
        <TextInputLabel label="Weight" />
        <TextField
          placeholder="0.0"
          name="weight"
          onChange={onFieldChange}
          value={form.weight}
          size="small"
        />
        <Typography variant="caption">
          Value must be greater than 0, and less than or equal to 1
        </Typography>
        {showAmountError && (
          <Typography variant="caption" color="error">
            Value is greater than 1. Reduce the weight.
          </Typography>
        )}
      </Box>
    </FormDialog>
  );
};
